/**
 * Utility functions for detecting and parsing links in text content
 */

export interface DetectedLink {
  text: string;
  url: string;
  startIndex: number;
  endIndex: number;
}

/**
 * Regular expression to match URLs
 * Supports http, https, ftp, and common domains
 */
const URL_REGEX = /(https?:\/\/[^\s]+|www\.[^\s]+|[a-zA-Z0-9-]+\.[a-zA-Z]{2,}(?:\/[^\s]*)?)/gi;

/**
 * Detect all URLs in a text string
 * @param text - The text to scan for URLs
 * @returns Array of detected links with their positions
 */
export function detectLinks(text: string): DetectedLink[] {
  const links: DetectedLink[] = [];

  // Reset regex lastIndex to ensure we start from the beginning
  URL_REGEX.lastIndex = 0;

  let match = URL_REGEX.exec(text);
  while (match !== null) {
    const url = match[0];
    const startIndex = match.index;
    const endIndex = startIndex + url.length;

    // Normalize URL (add https:// if missing)
    const normalizedUrl = normalizeUrl(url);

    links.push({
      text: url,
      url: normalizedUrl,
      startIndex,
      endIndex
    });

    match = URL_REGEX.exec(text);
  }

  return links;
}

/**
 * Normalize URL by adding protocol if missing
 * @param url - The URL to normalize
 * @returns Normalized URL with protocol
 */
function normalizeUrl(url: string): string {
  // If URL already has protocol, return as is
  if (url.match(/^https?:\/\//i)) {
    return url;
  }

  // If URL starts with www., add https://
  if (url.match(/^www\./i)) {
    return `https://${url}`;
  }

  // For other domains, add https://
  return `https://${url}`;
}

/**
 * Check if a string contains any URLs
 * @param text - The text to check
 * @returns True if text contains URLs
 */
export function hasLinks(text: string): boolean {
  URL_REGEX.lastIndex = 0;
  return URL_REGEX.test(text);
}

/**
 * Extract domain from URL for display purposes
 * @param url - The URL to extract domain from
 * @returns Domain name or 'Unknown site' if invalid
 */
export function extractDomain(url: string): string {
  try {
    const urlObj = new URL(url);
    return urlObj.hostname.replace('www.', '');
  } catch {
    return 'Unknown site';
  }
}
